import { type Action, type ThunkAction, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { persistStore, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist";
import { createAppReducer } from "./createAppReducer";
import { resourcesApi } from "../modules/resources";
import { providerKeysApi, usersApi } from "../modules/users";
import { promptRunnerApi, promptApi } from "../modules/prompts/api";
import { modelsApi } from "../modules/models/api/modelsApi";
import { paymentsApi } from "../modules/payments/api/paymentsAPI";
import { featuresApi } from "../modules/features";
import { filesApi } from "../modules/files";
import { chatsApi } from "../modules/chats/api/chatsApi";
import { evalsApi } from "../modules/evals/api/evalsApi";
import { evalRunnerApi } from "../modules/evals/api/evalRunnerApi";
import { judgementsApi } from "../modules/judgements/api/judgementsApi";

const rootReducer = createAppReducer();

export const store = configureStore({
  reducer: rootReducer,
  // @ts-ignore - The large number of RTK Query API slices in RootState pushes
  // the getDefaultMiddleware generic past TS's instantiation-depth limit
  // ("Type instantiation is excessively deep"). The runtime config is correct.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // We need this to avoid serializable check errors with redux-persist
      // refs: https://redux-toolkit.js.org/usage/usage-guide#use-with-redux-persist
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat([
      resourcesApi.middleware,
      promptApi.middleware,
      usersApi.middleware,
      providerKeysApi.middleware,
      promptRunnerApi.middleware,
      modelsApi.middleware,
      paymentsApi.middleware,
      featuresApi.middleware,
      filesApi.middleware,
      chatsApi.middleware,
      evalsApi.middleware,
      evalRunnerApi.middleware,
      judgementsApi.middleware,
    ]),
});

setupListeners(store.dispatch);

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = typeof store;
export type AppDispatch = AppStore["dispatch"];
export type AppThunk<ThunkReturnType = void> = ThunkAction<
  ThunkReturnType,
  RootState,
  unknown,
  Action
>;
